interface ValidationResult {
  isValid: boolean
  message: string
  urlTypeName?: string
}

export const validateUrl = (url: string, urlTypeName?: string): ValidationResult => {
  if (url === undefined || url === null || url.trim() === '') {
    return { isValid: false, message: `${urlTypeName ?? 'URL'} cannot be empty` }
  }

  switch (urlTypeName) {
    case 'email':
      return { ...validateEmail(url) }
    case 'emailWebform':
      return { ...validateEmailWebform(url) }
    case 'website':
      return { ...validateWebsite(url) }
    case 'facebook':
      return { ...validateFacebook(url) }
    case 'twitter':
      return { ...validateTwitter(url) }
    case 'youtube':
      return { ...validateYoutube(url) }
    case 'instagram':
      return { ...validateInstagram(url) }
    case 'linkedin':
      return { ...validateLinkedin(url) }
    default:
      return { isValid: false, message: 'Unsupported or missing URL type. Please check the value provided.' }
  }
}

const SOCIAL_URL_TYPES = ['facebook', 'twitter', 'youtube', 'instagram', 'linkedin'] as const

export const resolveUrlValidationType = (descLabel: string, fallbackType: string): string => {
  const lowerDesc = descLabel.toLowerCase()
  const social = SOCIAL_URL_TYPES.find(type => lowerDesc.includes(type))
  if (social !== undefined) return social
  if (lowerDesc.includes('email webform')) return 'emailWebform'
  return fallbackType
}

const validateEmail = (email: string): ValidationResult => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return {
    isValid: emailRegex.test(email),
    message: emailRegex.test(email) ? '' : 'Invalid email format. Expected: name@domain.com'
  }
}

const validateEmailWebform = (url: string): ValidationResult => {
  const httpRegex = /^https?:\/\//i

  return {
    isValid: httpRegex.test(url),
    message: httpRegex.test(url) ? '' : 'Invalid email webform URL. Expected: http://www.username.senate.gov/contact/contact-username'
  }
}

const validateWebsite = (website: string): ValidationResult => {
  const urlRegex = /^https?:\/\/[a-z0-9.-]+\.[a-z]{2,}(:\d+)?(\/[^\s]*)?$/i
  return {
    isValid: urlRegex.test(website),
    message: urlRegex.test(website) ? '' : 'Invalid website URL. Expected: http(s)://example.com'
  }
}

const validateFacebook = (url: string): ValidationResult => {
  const facebookRegex = /^http:\/\/(www\.)?facebook\.com\/([\w.-]+)/
  return {
    isValid: facebookRegex.test(url),
    message: facebookRegex.test(url) ? '' : 'Invalid Facebook URL. Expected: http://facebook.com/username'
  }
}

const validateTwitter = (url: string): ValidationResult => {
  const twitterRegex = /^http:\/\/(www\.)?twitter\.com\/([\w.-]+)/
  return {
    isValid: twitterRegex.test(url),
    message: twitterRegex.test(url) ? '' : 'Invalid Twitter URL. Expected: http://twitter.com/username'
  }
}

const validateYoutube = (url: string): ValidationResult => {
  const youtubeRegex = /^http:\/\/(www\.)?youtube\.com\/(user\/|channel\/|c\/)?([a-zA-Z0-9_-]+)/
  return {
    isValid: youtubeRegex.test(url),
    message: youtubeRegex.test(url) ? '' : 'Invalid YouTube URL. Expected: http://youtube.com/username'
  }
}

const validateInstagram = (url: string): ValidationResult => {
  const instagramRegex = /^http:\/\/(www\.)?instagram\.com\/([\w.-]+)/
  return {
    isValid: instagramRegex.test(url),
    message: instagramRegex.test(url) ? '' : 'Invalid Instagram URL. Expected: http://instagram.com/username'
  }
}

const validateLinkedin = (url: string): ValidationResult => {
  const linkedinRegex = /^http:\/\/(www\.)?linkedin\.com\/([\w.-]+)/
  return {
    isValid: linkedinRegex.test(url),
    message: linkedinRegex.test(url) ? '' : 'Invalid LinkedIn URL. Expected: http://linkedin.com/username'
  }
}

const SOCIAL_PLATFORM_DOMAINS: Array<[string, string]> = [
  ['facebook.com', 'Facebook'],
  ['twitter.com', 'Twitter'],
  ['x.com', 'Twitter'],
  ['instagram.com', 'Instagram'],
  ['linkedin.com', 'LinkedIn'],
  ['youtube.com', 'YouTube']
]

const WEBSITE_CONTENT_TYPES = ['website', 'weblog']

export const SOCIAL_URL_WARNING_TITLE = 'Social media URL under a website description'

export const socialUrlUnderWebsiteWarning = (url: string, descName: string, descContentType: string | null): string | undefined => {
  if (descContentType == null || !WEBSITE_CONTENT_TYPES.includes(descContentType)) return undefined

  let hostname: string
  try {
    hostname = new URL(url.trim()).hostname.toLowerCase()
  } catch {
    return undefined
  }

  const match = SOCIAL_PLATFORM_DOMAINS.find(([domain]) => hostname === domain || hostname.endsWith(`.${domain}`))
  if (match === undefined) return undefined

  const [, platform] = match
  return `This link points to ${platform}, but its description is "${descName}". Social media links belong under the matching ${platform} description. Save it as "${descName}" anyway?`
}
